import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import { api } from "../lib/api";
import { useReveal } from "../lib/useReveal";
import { Newspaper } from "lucide-react";

export default function News() {
  const ref = useReveal();
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get("/news").then(({ data }) => setArticles(data.articles)).catch(() => setArticles([]));
  }, []);

  return (
    <div ref={ref}>
      <Seo
        title="News & Stories"
        path="/news"
        description="News, announcements and verified project updates from Mount Moriah Relief."
      />
      <PageHero eyebrow="News &amp; Stories" title="Updates from our work">
        <p>
          Announcements, project updates and stories — published only when verified and approved.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10" data-testid="news-list">
          {articles === null ? (
            <p className="text-charcoal/60">Loading…</p>
          ) : articles.length === 0 ? (
            <div className="reveal max-w-2xl bg-white border border-sandstone p-8 lg:p-12">
              <Newspaper size={28} strokeWidth={1.5} className="text-gold" aria-hidden="true" />
              <h2 className="mt-6 font-serif text-2xl sm:text-3xl font-semibold text-forest">
                Our first updates are on their way
              </h2>
              <p className="mt-4 text-charcoal/80 leading-relaxed">
                Mount Moriah Relief was established in 2024, and our first programmes are being
                developed with care. As partnerships are confirmed and work begins, verified news
                and project updates will appear here — we do not publish placeholder stories.
              </p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((a) => (
                <article key={a.id} className="reveal bg-white border border-sandstone hover:-translate-y-1 hover:shadow-md hover:shadow-black/5 transition-[transform,box-shadow] duration-300">
                  {a.cover_image && (
                    <img src={a.cover_image} alt="" loading="lazy" className="w-full aspect-[16/10] object-cover" />
                  )}
                  <div className="p-7">
                    <p className="text-xs tracking-wider uppercase font-semibold text-gold">{a.category}</p>
                    <h2 className="mt-3 font-serif text-2xl font-semibold text-charcoal leading-snug">
                      <Link to={`/news/${a.slug}`} className="hover:text-forest transition-colors duration-300">
                        {a.title}
                      </Link>
                    </h2>
                    {a.summary && <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">{a.summary}</p>}
                    {a.published_at && (
                      <p className="mt-4 text-xs text-charcoal/50">
                        {new Date(a.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
